import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import OktaAuth, {UserClaims} from '@okta/okta-auth-js';
import {
	catchError,
	forkJoin,
	map,
	Observable,
	of,
	ReplaySubject,
	shareReplay,
	Subject,
	from as fromPromise,
	take, switchMap
} from 'rxjs';

import {handleError, httpOptions, httpOptionsForFileManagement} from '../app.constants';
import {HttpEventService} from './http/http-event.service';
import {Client} from "../modal/organization/Client";
import {OKTA_AUTH} from "@okta/okta-angular";
import {StoreManagerService} from "./storage.service";

export type USER_TYPE = 'PO' | 'PS' | 'SUPPORT' | 'INTERNAL' | '';

export interface ClientTypeInfo {
	isInternalUser: boolean,
	isTenant: boolean
}

@Injectable({
	providedIn: 'root'
})
export class UserService {

	private serviceType: string = '';
	private userType: USER_TYPE;

	serviceType$: Observable<string> = fromPromise(this.oktaAuth.getUser()).pipe(
		map(userClaims => {
			const USER_ROLES = userClaims.roles ? userClaims.roles.toString().split(',') : [];
			if (USER_ROLES.indexOf('CNR_ROLE_FICO_PO_ANALYST') > -1) {
				this.userType = 'PO';
				this.serviceType = 'po';
			} else if ((USER_ROLES.indexOf('CNR_ROLE_TENANT_CUSTOMER') > -1)
				|| (USER_ROLES.indexOf('CNR_ROLE_NON_TENANT_CUSTOMER') > -1)
				|| (USER_ROLES.indexOf('CNR_ROLE_FICO_TENANT_ADMIN') > -1)) {
				this.userType = USER_ROLES.indexOf('CNR_ROLE_FICO_PS') > -1 ? 'PS' : USER_ROLES.indexOf('CNR_ROLE_FICO_SUPPORT') > -1 ? 'SUPPORT' : '';
				this.serviceType = USER_ROLES.indexOf('CNR_ROLE_FICO_PS') > -1 || USER_ROLES.indexOf('CNR_ROLE_FICO_SUPPORT') > -1 ? 'clientmanagement' : 'fileoperationsservice';
			}
			return this.serviceType;
		})
	);

	readonly userInfo$ = this.serviceType$.pipe(
		take(1),
		switchMap(serviceType => forkJoin([
			this.getLoggedUserPrivileges(serviceType),
			this.getLoggedClientInfo(serviceType)
		]))
	);

	private readonly userPrivilegesSubject: Subject<string[]> = new ReplaySubject<string[]>(1);
	readonly userPrivileges$: Observable<string[]> = this.userPrivilegesSubject.pipe(
		map(userPrivileges => userPrivileges),
		shareReplay(1),
		catchError(err => {
			handleError("userPrivileges$", 'privileges', this.httpEventService);
			return of([]);
		})
	);
	private readonly clientInfoSubject: Subject<any> = new ReplaySubject<any>(1);
	readonly clientInfo$: Observable<any> = this.clientInfoSubject.pipe(
		map(clientInfo => clientInfo),
		shareReplay(1),
		catchError(err => {
			handleError("clientInfo$", 'clientInfo', this.httpEventService);
			return of(null);
		})
	);
	private readonly clientRoleSubject: Subject<ClientTypeInfo> = new ReplaySubject<ClientTypeInfo>(1);
	readonly clientRole$: Observable<ClientTypeInfo> = this.clientRoleSubject.asObservable();

	constructor(private httpClient: HttpClient,
							private httpEventService: HttpEventService,
							@Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
							private storeManagerService: StoreManagerService ) {}

	setUser(_user: UserClaims) {
		this.userInfo$.subscribe({
			next: ([userPrivileges, clientInfo]) => {
				this.userPrivilegesSubject.next(userPrivileges);
				this.clientInfoSubject.next(clientInfo);

				this.clientRoleSubject.next({
					isInternalUser: userPrivileges.indexOf("ORG_CONTEXT_SWITCH") !== -1,
					isTenant: clientInfo.tenant !== null
				});

				this.storeManagerService.contextSubject.next(null);
			},
			error: (err) => {
				console.error('Failed to load user info:', err);
				this.userPrivilegesSubject.next([]);
			}
		});
	}

	getUserType(): USER_TYPE {
		return this.userType;
	}

	getLoggedClientInfo(serviceType: string): Observable<any> {
		let url = "", httpHeaders;
		if (serviceType === "fileoperationsservice") {
			url = "fileoperationsservice/currentuser";
			httpHeaders = httpOptionsForFileManagement();
		} else if (serviceType === "clientmanagement" || serviceType === "po") {
			url = "clientmanagement/clients/currentuser";
			httpHeaders = httpOptions();
		}

		return this.httpClient.get<any>(url, httpHeaders)
			.pipe(
				map(res => res),
				catchError(handleError("CommonService.getLoggedClientInfo", url, this.httpEventService))
			);
	}

	getLoggedUserPrivileges(serviceType: string): Observable<string[]> {
		let url: any, httpHeaders;
		if (serviceType === "fileoperationsservice") {
			url = "fileoperationsservice/currentuser/privileges";
			httpHeaders = httpOptionsForFileManagement();
		} else if (serviceType === "clientmanagement" || serviceType === "po") {
			url = "clientmanagement/currentuser/privileges";
			httpHeaders = httpOptions();
		}
		return this.httpClient.get<string[]>(url, httpHeaders)
			.pipe(
				map(res => res),
				catchError(handleError("CommonService.getLoggedUserPrivileges", url, this.httpEventService))
			);
	}

}
